/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

// Coordinates a single ordering guarantee across usePushNotifications.ts
// and the badge-clearing effects in App.tsx: nothing calls any
// @capawesome/capacitor-badge method (set/clear/increase/decrease) until
// after @capacitor-firebase/messaging's own, broader permission request
// (alert+badge+sound) has been made.
//
// This matters because of how the Badge plugin is actually implemented on
// iOS - every one of its action methods (set/clear/increase/decrease)
// internally calls its OWN requestAuthorization(options: .badge) first,
// unconditionally, as a side effect (see BadgePlugin.swift's set()).
// That's a separate, narrower authorization request than the one
// usePushNotifications.ts makes - and on a fresh install, whichever
// request reaches iOS first is the one the user's single permission
// dialog reflects. Badge.clear() is called from a much simpler effect
// than the full checkPermissions -> requestPermissions chain Firebase
// Messaging goes through, so without this gate it can easily win that
// race - which is exactly what was happening: the badge kept working
// (it only ever needed .badge), but .sound was never part of what the
// user was actually asked to grant, silently capping what notifications
// could do from that point forward (no amount of app-side payload
// content fixes a permission that was never granted in the first place).
let resolveSettled: () => void;
export const pushPermissionSettled = new Promise<void>((resolve) => {
  resolveSettled = resolve;
});

let settled = false;

// Idempotent - safe to call more than once (e.g. on every app resume);
// only the first call actually resolves the shared promise.
export function markPushPermissionSettled() {
  if (settled) {
    return;
  }
  settled = true;
  resolveSettled();
}
